import { useEffect, useRef, useState, useCallback } from "react"
import { Field, Form, Formik, FormikProps } from "formik"
import {
  Button,
  Input,
  Select,
  SegmentedToggle,
} from "~/ui-rtk/components/ui/controls"
import Breadcrumbs from "~/ui-rtk/components/layout/Breadcrumbs"
import { Dialog, RichTooltip } from "~/ui-rtk/components/ui/common"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import useDemoMode from "~/ui-rtk/hooks/demo-mode"
import { ErrorBlock } from "~/ui-rtk/utils/form"
import { capitalize, formatNumber } from "~/ui-rtk/utils/format"
import { useAppSelector } from "~/ui-rtk/app/hooks"
import { selectUserSessionRole } from "~/ui-rtk/app/selectors/user.selector"
import { SessionRole } from "~/api/dto/auth/session-role"
import { WEEK_START_DAYS } from "~/shared/kpi"
import {
  MAX_BUDGET_STEP_PCT_MAX,
  MAX_BUDGET_STEP_PCT_MIN,
} from "~/shared/budget-recommender/types"
import useConnect, {
  type TFinancialSettingsFormValues,
  type ReturnRateMode,
} from "./connect"
import { useRecommendedRoas } from "./useRecommendedRoas"
import {
  FIRST_ORDER_VALUE_LOOKBACK_DAYS,
  useRecommendedDailyBudget,
} from "~/ui-rtk/hooks/useRecommendedDailyBudget"
import { DAILY_BUDGET_EVAL_PERIOD_OPTIONS } from "~/ui-rtk/constants/daily-budget-eval"
import {
  amerFromCac,
  cacFromAmer,
  formatGuardrailValue,
  merTypeForDisplay,
  type TargetMerDisplay,
  type TargetMerScope,
} from "~/ui-rtk/utils/spend-guardrail"

function LockToggle({
  locked,
  onToggle,
}: {
  locked: boolean
  onToggle: (locked: boolean) => void
}) {
  return (
    <label className="flex items-center gap-2 cursor-pointer select-none mt-2">
      <input
        type="checkbox"
        className="toggle toggle-sm toggle-primary"
        checked={locked}
        onChange={e => onToggle(e.target.checked)}
      />
      <span
        className={cn(
          "text-xs",
          locked ? "text-system-sea-400" : "text-system-gray-500",
        )}
      >
        {locked ? "Locked to recommended" : "Lock to recommended"}
      </span>
    </label>
  )
}

const MARGINAL_PROFIT_OPTIONS = [
  { label: "Select an option", value: "", disabled: true },
  { label: "First order Breakeven", value: "first_order" },
  { label: "90d Breakeven", value: "90d" },
  { label: "180d Breakeven", value: "180d" },
  { label: "365d Breakeven", value: "365d" },
]

const WEEK_START_DAY_OPTIONS = WEEK_START_DAYS.map(day => ({
  label: capitalize(day),
  value: day,
}))

const BRAND_SEARCH_POLICY_OPTIONS = [
  { label: "Fixed (hold at current budget)", value: "fixed" },
  { label: "Optimize (reallocate like DR)", value: "optimize" },
]

// Settings configuration for navigation and "What this means" content
const SETTINGS_CONFIG = [
  {
    id: "brand-spend",
    label: "Target Brand Spend",
    section: "Brand Allocation",
    description:
      'How much you\'d like to spend on creative that tells your brand story and focuses on building "top of funnel" brand awareness and brand engagement among audiences who aren\'t yet "in-market" but are soon to be.',
  },
  {
    id: "gross-margin",
    label: "Gross Margin",
    section: "Margins & Costs",
    description:
      "The amount of gross profit you're left over with after you subtract your cost of goods sold (COGS) from your order revenue, expressed as a % of order revenue.",
  },
  {
    id: "ltv-threshold",
    label: "LTV Threshold",
    section: "Margins & Costs",
    description:
      "While we want to have targets for our blended spend as a % of revenue and our blended return on ad spend (ROAS), we also want to set targets for how we increase our ad spend. Here, we want to think about the marginal return of any increases in spend and a simple way to think about this is how much would we be willing to spend to acquire a single new customer if we knew it was entirely marginal (as in, you definitely *wouldn't* acquire the customer if you *didn't* spend the money, and you definitely *would* acquire the customer if you *did* spend the money). If you knew you could acquire marginal customers for greater than breakeven, then theoretically you'd spend as much as you could to infinity. As we know, the more we spend, the worse our marginal performance gets, so we use this setting to understand how much we're willing to spend into a customer's LTV for that marginal purchase.",
  },
  {
    id: "shipping-costs",
    label: "Shipping Costs",
    section: "Margins & Costs",
    description:
      "The variable shipping costs you need to pay to fulfill an order to the end customer. This usually includes pick, pack, and ship fees, as well as postage.",
  },
  {
    id: "cc-fees",
    label: "CC Fees",
    section: "Margins & Costs",
    description:
      "The % of our revenue we pay to credit card companies or payment providers like Shop Pay, Paypal, Affirm, etc.",
  },
  {
    id: "return-rate",
    label: "Return Rate",
    section: "Margins & Costs",
    description:
      'By default we compute your return rate from your order history. Set a default return rate here and choose how it\'s applied: "Use computed" always uses our figure, "Use default" always uses yours, and "Max of the two" takes whichever is higher. Use this when our computed rate is incomplete or you calculate returns differently.',
  },
  {
    id: "product-cost-markup",
    label: "Product Cost Markup",
    section: "Margins & Costs",
    description:
      "Add a flat markup on top of the product cost we compute, expressed as a percentage of order value. For example, a 5% markup on a $100 order adds $5 of product cost. Use this when your true landed cost runs higher than what we derive from your data.",
  },
  {
    id: "amazon-discount",
    label: "Amazon Discount %",
    section: "Amazon Financial Settings",
    description:
      "The average discount applied to your Amazon orders, expressed as a percentage of gross sales.",
  },
  {
    id: "amazon-refund-rate",
    label: "Amazon Refund Rate",
    section: "Amazon Financial Settings",
    description:
      "The percentage of your Amazon gross sales that is refunded to customers.",
  },
  {
    id: "amazon-shipping-costs",
    label: "Amazon Shipping Costs",
    section: "Amazon Financial Settings",
    description:
      "Your Amazon shipping and fulfillment costs, expressed as a percentage of gross sales.",
  },
  {
    id: "amazon-cogs",
    label: "Amazon COGS",
    section: "Amazon Financial Settings",
    description:
      "Your Amazon cost of goods sold, expressed as a percentage of gross sales.",
  },
  {
    id: "amazon-cc-fees",
    label: "Amazon CC/Seller Fees",
    section: "Amazon Financial Settings",
    description:
      "Credit card and Amazon seller/referral fees, expressed as a percentage of gross sales.",
  },
  {
    id: "amazon-ad-spend",
    label: "Amazon Ad Spend",
    section: "Amazon Financial Settings",
    description:
      "Your Amazon advertising spend, expressed as a percentage of gross sales.",
  },
  {
    id: "dtc-retail-staffing",
    label: "DTC Retail Staffing",
    section: "DTC Retail Financial Settings",
    description:
      "Variable staffing costs for your DTC retail business, expressed as a percentage of sales.",
  },
  {
    id: "dtc-retail-cc-fees",
    label: "DTC Retail CC/Seller Fees",
    section: "DTC Retail Financial Settings",
    description:
      "Credit card and seller fees for your DTC retail business, expressed as a percentage of gross sales.",
  },
  {
    id: "dtc-retail-local-marketing",
    label: "DTC Retail Local Marketing",
    section: "DTC Retail Financial Settings",
    description:
      "Local events and community marketing spend for your DTC retail business, expressed as a percentage of gross sales.",
  },
  {
    id: "dtc-retail-other-costs",
    label: "DTC Retail Other Costs",
    section: "DTC Retail Financial Settings",
    description:
      "Other variable costs for your DTC retail business, expressed as a percentage of gross sales.",
  },
  {
    id: "target-mer",
    label: "Spend Guardrail",
    section: "ROAS & Budget Targets",
    description:
      "The target your recommended daily budget is derived from, expressed however you manage it. aMER (Acquisition MER) is new customer revenue divided by total ad spend; MER is total revenue divided by total ad spend; CAC is total ad spend divided by new customers. CAC and aMER are the same guardrail seen from two sides (CAC = average first order value / aMER), so a CAC target is stored as its equivalent aMER and the budget math is identical. Choose whether it applies to your ecommerce business or your total business.",
  },
  {
    id: "target-dr-roas",
    label: "Target DR ROAS",
    section: "ROAS & Budget Targets",
    description:
      "The target return on ad spend for direct response campaigns. This is auto-calculated from your cost structure and historical revenue data but can be overridden. A higher ROAS target means you require more revenue per dollar of ad spend.",
  },
  {
    id: "target-brand-roas",
    label: "Target Brand ROAS",
    section: "ROAS & Budget Targets",
    description:
      "The target return on ad spend for brand awareness campaigns. Defaults to 1.5x your DR ROAS target since brand campaigns are expected to generate more long-term value per dollar spent. Can be overridden.",
  },
  {
    id: "target-daily-budget",
    label: "Target Daily Budget",
    section: "ROAS & Budget Targets",
    description:
      "Your total target daily advertising budget across all channels. This is used in the Budget Recommender to scale individual campaign/adset recommendations so they sum to your desired daily total. The recommended value is derived from your spend guardrail (aMER, MER, or CAC target) over a configurable evaluation period (last 1, 3, or 7 complete days).",
  },
  {
    id: "week-start-day",
    label: "Reporting Week Start",
    section: "Reporting",
    description:
      "The first day of your reporting week. Weekly views across the product group data into weeks starting on this day and ending six days later — a Sunday start makes weeks run Sunday through Saturday. Changing it immediately re-groups all weekly reporting, including historical data, for everyone in your company. Only company admins can change it.",
  },
]

// Card wrapper component for consistent styling
function SettingCard({
  id,
  title,
  tooltip,
  isActive,
  children,
  className,
}: {
  id: string
  title: string
  tooltip?: string
  isActive?: boolean
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      id={id}
      className={cn(
        "bg-system-blue-800/60 border rounded-xl p-6",
        "transition-all duration-200",
        "scroll-mt-24",
        isActive
          ? "border-system-brand-pink-500/60 shadow-[0_0_12px_rgba(236,72,153,0.15)]"
          : "border-system-blue-600/40 hover:border-system-blue-500/60",
        className,
      )}
    >
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        {title}
        {tooltip && (
          <RichTooltip
            content={<p className="text-sm leading-relaxed">{tooltip}</p>}
            place="right"
          >
            <svg
              className="w-4 h-4 text-system-gray-400 hover:text-system-gray-200 transition-colors cursor-help"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </RichTooltip>
        )}
      </h3>
      {children}
    </div>
  )
}

// Left navigation component with contextual help below
function PageNavigation({
  activeId,
  onNavigate,
  onSave,
  isSaving,
  isDisabled,
}: {
  activeId: string
  onNavigate: (id: string) => void
  onSave: () => void
  isSaving: boolean
  isDisabled: boolean
}) {
  const activeSetting = SETTINGS_CONFIG.find(s => s.id === activeId)

  return (
    <aside className="h-fit sticky top-24 space-y-4">
      <div className="bg-system-blue-800/40 border border-system-blue-600/30 rounded-xl p-4">
        <h4 className="text-sm font-semibold text-system-gray-300 uppercase tracking-wider mb-4">
          Page Content
        </h4>
        <nav className="space-y-1">
          {SETTINGS_CONFIG.map(setting => (
            <button
              key={setting.id}
              onClick={() => onNavigate(setting.id)}
              className={cn(
                "w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200",
                activeId === setting.id
                  ? "bg-system-brand-pink-600/20 text-system-brand-pink-300 border-l-2 border-system-brand-pink-500"
                  : "text-system-gray-400 hover:text-white hover:bg-system-blue-700/50",
              )}
            >
              {setting.label}
            </button>
          ))}
        </nav>
      </div>

      <Button
        variant={{ variant: "solid", color: "pink" }}
        size="sm"
        isLoading={isSaving}
        disabled={isDisabled}
        onClick={onSave}
        type="button"
        className="w-full"
      >
        Save
      </Button>

      <div className="bg-system-blue-800/40 border border-system-blue-600/30 rounded-xl p-5">
        <h4 className="text-sm font-semibold text-system-gray-300 uppercase tracking-wider mb-3">
          What this means?
        </h4>
        <div className="min-h-[120px]">
          {activeSetting ? (
            <div key={activeSetting.id} className="animate-fadeIn">
              <h5 className="text-base font-medium text-white mb-2">
                {activeSetting.label}
              </h5>
              <p className="text-sm text-system-gray-400 leading-relaxed">
                {activeSetting.description}
              </p>
            </div>
          ) : (
            <p className="text-sm text-system-gray-500 italic">
              Hover over or select a setting to see more information.
            </p>
          )}
        </div>
      </div>
    </aside>
  )
}

// Skeleton placeholder for a setting card while data loads
function SettingCardSkeleton() {
  return (
    <div className="bg-system-blue-800/60 border border-system-blue-600/40 rounded-xl p-6 animate-pulse">
      <div className="h-5 w-48 bg-system-blue-600/50 rounded mb-4" />
      <div className="h-4 w-72 bg-system-blue-600/30 rounded mb-4" />
      <div className="h-10 w-32 bg-system-blue-600/40 rounded" />
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="space-y-8">
      <div>
        <div className="h-6 w-44 bg-system-blue-600/40 rounded mb-4 animate-pulse" />
        <div className="space-y-4">
          <SettingCardSkeleton />
          <SettingCardSkeleton />
        </div>
      </div>
      <div>
        <div className="h-6 w-36 bg-system-blue-600/40 rounded mb-4 animate-pulse" />
        <div className="space-y-4">
          <SettingCardSkeleton />
        </div>
      </div>
      <div>
        <div className="h-6 w-36 bg-system-blue-600/40 rounded mb-4 animate-pulse" />
        <div className="space-y-4">
          <SettingCardSkeleton />
          <SettingCardSkeleton />
          <SettingCardSkeleton />
          <SettingCardSkeleton />
        </div>
      </div>
      <div>
        <div className="h-6 w-56 bg-system-blue-600/40 rounded mb-4 animate-pulse" />
        <div className="space-y-4">
          <SettingCardSkeleton />
          <SettingCardSkeleton />
          <SettingCardSkeleton />
          <SettingCardSkeleton />
          <SettingCardSkeleton />
          <SettingCardSkeleton />
        </div>
      </div>
      <div>
        <div className="h-6 w-56 bg-system-blue-600/40 rounded mb-4 animate-pulse" />
        <div className="space-y-4">
          <SettingCardSkeleton />
          <SettingCardSkeleton />
          <SettingCardSkeleton />
          <SettingCardSkeleton />
        </div>
      </div>
      <div>
        <div className="h-6 w-52 bg-system-blue-600/40 rounded mb-4 animate-pulse" />
        <div className="space-y-4">
          <SettingCardSkeleton />
          <SettingCardSkeleton />
          <SettingCardSkeleton />
          <SettingCardSkeleton />
        </div>
      </div>
    </div>
  )
}

function FormContent({
  values,
  errors,
  setFieldValue,
  activeSettingId,
  canEditWeekStartDay,
}: {
  values: TFinancialSettingsFormValues
  errors: Record<string, string | undefined>
  setFieldValue: (
    field: string,
    value: unknown,
  ) => Promise<void | Record<string, string>>
  activeSettingId: string
  canEditWeekStartDay: boolean
}) {
  const [isCustomDRRoas, setIsCustomDRRoas] = useState(false)
  const [isCustomBrandRoas, setIsCustomBrandRoas] = useState(false)
  const [isCustomDailyBudget, setIsCustomDailyBudget] = useState(false)

  const {
    recommendedDRRoas,
    recommendedBrandRoas,
    isLoading: isRoasLoading,
    hasData: hasRoasData,
  } = useRecommendedRoas({
    grossMargin: values.blendedGrossMargin,
    shippingCost: values.shippingCostPercentAssm,
    ccFees: values.ccFeePercentAssm,
    spendIntoMarginalLtv: values.spendIntoMarginalLtv,
  })

  const {
    recommendedDailyBudget,
    firstOrderValue,
    isLoading: isDailyBudgetLoading,
    hasData: hasDailyBudgetData,
  } = useRecommendedDailyBudget({
    targetMerType: values.targetMerType,
    targetMerValue: values.targetMerValue,
    targetMerScope: values.targetMerScope,
    evalPeriodDays: values.dailyBudgetEvalPeriodDays,
  })

  // The guardrail is stored as a ratio; a CAC display edits it through the
  // first order value. While the CAC field is being typed in, show the raw
  // text so the derived value does not reformat under the cursor.
  const [cacDraft, setCacDraft] = useState<string | null>(null)
  const isCacDisplay = values.targetMerDisplay === "cac"
  const targetCac = cacFromAmer(values.targetMerValue, firstOrderValue)
  const scopeLabel =
    values.targetMerScope === "ecomm" ? "Ecommerce" : "Total Business"
  const firstOrderValueLabel =
    firstOrderValue !== null
      ? `${formatNumber(firstOrderValue, { currency: true })} average first order value (last ${FIRST_ORDER_VALUE_LOOKBACK_DAYS} days, ${scopeLabel})`
      : null

  let cacHelpText: string
  if (firstOrderValueLabel) {
    cacHelpText = `Stored as an aMER of ${formatGuardrailValue(values.targetMerValue, "amer")} at your ${firstOrderValueLabel}, so the budget math is the same as the equivalent aMER.`
  } else if (isDailyBudgetLoading) {
    cacHelpText = "Loading your average first order value…"
  } else {
    cacHelpText = `No new customer orders in the last ${FIRST_ORDER_VALUE_LOOKBACK_DAYS} days, so a CAC target can't be converted yet. Switch to aMER to set the target directly.`
  }

  const handleDisplayChange = (value: string) => {
    const display = value as TargetMerDisplay
    // A CAC target is an aMER seen per customer, so it stays on the aMER type.
    const nextType = merTypeForDisplay(display)
    setCacDraft(null)
    void setFieldValue("targetMerDisplay", display)
    // aMER and MER have different revenue numerators, so a ratio carried
    // across that switch would silently re-target the guardrail (and, with no
    // first order value, the CAC field is disabled and cannot correct it).
    // aMER <-> CAC keeps the value: same guardrail, two expressions.
    if (nextType !== values.targetMerType) {
      void setFieldValue("targetMerValue", 0)
    }
    void setFieldValue("targetMerType", nextType)
  }

  // Capture initial mount value to detect pre-existing saved data
  const [hadSavedDRRoas] = useState(() => values.targetDRRoas > 0)
  const [hadSavedBrandRoas] = useState(() => values.targetBrandRoas > 0)
  const [hadSavedDailyBudget] = useState(() => values.targetDailyBudget > 0)

  useEffect(() => {
    if (values.targetDRRoasLocked) {
      if (recommendedDRRoas !== null && recommendedDRRoas > 0) {
        void setFieldValue("targetDRRoas", recommendedDRRoas)
      }
      return
    }
    if (isCustomDRRoas || hadSavedDRRoas) return
    if (recommendedDRRoas !== null && recommendedDRRoas > 0) {
      void setFieldValue("targetDRRoas", recommendedDRRoas)
    }
  }, [
    recommendedDRRoas,
    isCustomDRRoas,
    hadSavedDRRoas,
    values.targetDRRoasLocked,
    setFieldValue,
  ])

  useEffect(() => {
    if (values.targetBrandRoasLocked) {
      if (recommendedBrandRoas !== null && recommendedBrandRoas > 0) {
        void setFieldValue("targetBrandRoas", recommendedBrandRoas)
      }
      return
    }
    if (isCustomBrandRoas || hadSavedBrandRoas) return
    if (recommendedBrandRoas !== null && recommendedBrandRoas > 0) {
      void setFieldValue("targetBrandRoas", recommendedBrandRoas)
    }
  }, [
    recommendedBrandRoas,
    isCustomBrandRoas,
    hadSavedBrandRoas,
    values.targetBrandRoasLocked,
    setFieldValue,
  ])

  useEffect(() => {
    if (values.targetDailyBudgetLocked) {
      if (recommendedDailyBudget !== null && recommendedDailyBudget > 0) {
        void setFieldValue("targetDailyBudget", recommendedDailyBudget)
      }
      return
    }
    if (isCustomDailyBudget || hadSavedDailyBudget) return
    if (recommendedDailyBudget !== null && recommendedDailyBudget > 0) {
      void setFieldValue("targetDailyBudget", recommendedDailyBudget)
    }
  }, [
    recommendedDailyBudget,
    isCustomDailyBudget,
    hadSavedDailyBudget,
    values.targetDailyBudgetLocked,
    setFieldValue,
  ])

  return (
    <Form className="space-y-8 animate-fadeIn">
      {/* Section: Brand Allocation */}
      <div>
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <span className="w-1 h-6 bg-system-brand-pink-500 rounded-full" />
          Brand Allocation
        </h2>
        <div className="space-y-4">
          {/* Brand Spend Card */}
          <SettingCard
            id="brand-spend"
            title="Target Brand Spend as % of Total"
            tooltip={
              SETTINGS_CONFIG.find(s => s.id === "brand-spend")?.description
            }
            isActive={activeSettingId === "brand-spend"}
          >
            <p className="text-sm text-system-gray-400 mb-4">
              Choose how to divide your budget between brand awareness and
              direct response.
            </p>
            <div className="space-y-3">
              {/* Slider track */}
              <div className="relative h-8 flex items-center">
                <div className="absolute inset-x-0 mx-3 h-4 flex rounded-full overflow-hidden shadow-inner">
                  <div
                    className="bg-gradient-to-r from-system-brand-pink-600 to-system-brand-pink-400 transition-all duration-300 ease-out"
                    style={{
                      width: `${values.targetBrandSpendPercentOfTotal * 100}%`,
                    }}
                  />
                  <div className="bg-gradient-to-r from-system-sea-600 to-system-sea-500 flex-1" />
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={values.targetBrandSpendPercentOfTotal * 100}
                  onChange={e => {
                    void setFieldValue(
                      "targetBrandSpendPercentOfTotal",
                      parseInt(e.target.value, 10) / 100,
                    )
                  }}
                  className="relative w-full h-4 bg-transparent rounded-full appearance-none cursor-pointer z-10
                              [&::-webkit-slider-thumb]:appearance-none
                              [&::-webkit-slider-thumb]:w-6
                              [&::-webkit-slider-thumb]:h-6
                              [&::-webkit-slider-thumb]:rounded-full
                              [&::-webkit-slider-thumb]:bg-white
                              [&::-webkit-slider-thumb]:shadow-[0_0_8px_rgba(236,72,153,0.6)]
                              [&::-webkit-slider-thumb]:cursor-pointer
                              [&::-webkit-slider-thumb]:transition-all
                              [&::-webkit-slider-thumb]:duration-200
                              [&::-webkit-slider-thumb]:hover:scale-110
                              [&::-webkit-slider-thumb]:hover:shadow-[0_0_14px_rgba(236,72,153,0.8)]
                              [&::-moz-range-thumb]:w-6
                              [&::-moz-range-thumb]:h-6
                              [&::-moz-range-thumb]:rounded-full
                              [&::-moz-range-thumb]:bg-white
                              [&::-moz-range-thumb]:border-0
                              [&::-moz-range-thumb]:shadow-[0_0_8px_rgba(236,72,153,0.6)]
                              [&::-moz-range-thumb]:cursor-pointer"
                />
              </div>
              {/* Labels */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-system-brand-pink-500" />
                  <span className="text-sm text-system-brand-pink-300 font-medium">
                    Brand{" "}
                    {(values.targetBrandSpendPercentOfTotal * 100).toFixed(0)}%
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-system-sea-400 font-medium">
                    DR{" "}
                    {(
                      (1 - values.targetBrandSpendPercentOfTotal) *
                      100
                    ).toFixed(0)}
                    %
                  </span>
                  <span className="w-3 h-3 rounded-full bg-system-sea-500" />
                </div>
              </div>
            </div>
            <ErrorBlock error={errors.targetBrandSpendPercentOfTotal} />
          </SettingCard>
        </div>
      </div>

      {/* Section: Margins & Costs */}
      <div>
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <span className="w-1 h-6 bg-system-brand-pink-500 rounded-full" />
          Margins & Costs
        </h2>
        <div className="space-y-4">
          {/* Gross Margin Card */}
          <SettingCard
            id="gross-margin"
            title="Gross Margin %"
            tooltip={
              SETTINGS_CONFIG.find(s => s.id === "gross-margin")?.description
            }
            isActive={activeSettingId === "gross-margin"}
          >
            <p className="text-sm text-system-gray-400 mb-4">
              Estimate how much return you need on ad spend to stay profitable.
            </p>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                step="1"
                min="0"
                max="100"
                value={(values.blendedGrossMargin * 100).toFixed(0)}
                onChange={e => {
                  void setFieldValue(
                    "blendedGrossMargin",
                    parseFloat(e.target.value) / 100,
                  )
                }}
                className="w-32"
                placeholder="Enter margin %"
              />
              <span className="text-system-gray-400 text-sm font-medium">
                %
              </span>
            </div>
            <ErrorBlock error={errors.blendedGrossMargin} />
          </SettingCard>

          {/* LTV Threshold Card */}
          <SettingCard
            id="ltv-threshold"
            title="LTV Threshold"
            tooltip={
              SETTINGS_CONFIG.find(s => s.id === "ltv-threshold")?.description
            }
            isActive={activeSettingId === "ltv-threshold"}
          >
            <p className="text-sm text-system-gray-400 mb-4">
              To acquire one more new customer by increasing your advertising
              budget, how much are you willing to spend into that customer's LTV
              to get them to place their first order?
            </p>
            <Field
              as={Select}
              name="spendIntoMarginalLtv"
              options={MARGINAL_PROFIT_OPTIONS}
              className="w-64"
            />
            {!isRoasLoading &&
              !hasRoasData &&
              (values.spendIntoMarginalLtv === "180d" ||
                values.spendIntoMarginalLtv === "365d") && (
                <p className="mt-2 text-xs text-system-gray-500">
                  Not enough order history yet to compute a{" "}
                  {values.spendIntoMarginalLtv === "180d" ? "180" : "365"}-day
                  LTV recommendation — this needs cohorts old enough to have
                  matured over that window. We&apos;ll show a recommended ROAS
                  once enough history is available.
                </p>
              )}
            <ErrorBlock error={errors.spendIntoMarginalLtv} />
          </SettingCard>

          {/* Shipping Costs Card */}
          <SettingCard
            id="shipping-costs"
            title="Shipping Costs as % of Order Revenue"
            tooltip={
              SETTINGS_CONFIG.find(s => s.id === "shipping-costs")?.description
            }
            isActive={activeSettingId === "shipping-costs"}
          >
            <p className="text-sm text-system-gray-400 mb-4">
              Variable fulfillment costs including pick, pack, ship, and
              postage.
            </p>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                step="1"
                min="0"
                max="100"
                value={(values.shippingCostPercentAssm * 100).toFixed(0)}
                onChange={e => {
                  void setFieldValue(
                    "shippingCostPercentAssm",
                    parseFloat(e.target.value) / 100,
                  )
                }}
                className="w-32"
                placeholder="Enter %"
              />
              <span className="text-system-gray-400 text-sm font-medium">
                %
              </span>
            </div>
            <ErrorBlock error={errors.shippingCostPercentAssm} />
          </SettingCard>

          {/* CC Fees Card */}
          <SettingCard
            id="cc-fees"
            title="Credit Card Fees as % of Order Revenue"
            tooltip={SETTINGS_CONFIG.find(s => s.id === "cc-fees")?.description}
            isActive={activeSettingId === "cc-fees"}
          >
            <p className="text-sm text-system-gray-400 mb-4">
              Payment processing fees from credit cards, Shop Pay, PayPal,
              Affirm, etc.
            </p>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={(values.ccFeePercentAssm * 100).toFixed(1)}
                onChange={e => {
                  void setFieldValue(
                    "ccFeePercentAssm",
                    parseFloat(e.target.value) / 100,
                  )
                }}
                className="w-32"
                placeholder="Enter %"
              />
              <span className="text-system-gray-400 text-sm font-medium">
                %
              </span>
            </div>
            <ErrorBlock error={errors.ccFeePercentAssm} />
          </SettingCard>

          {/* Default Return Rate Card */}
          <SettingCard
            id="return-rate"
            title="Default Return Rate"
            tooltip={
              SETTINGS_CONFIG.find(s => s.id === "return-rate")?.description
            }
            isActive={activeSettingId === "return-rate"}
          >
            <p className="text-sm text-system-gray-400 mb-4">
              Choose how your return rate is determined, and set a default to
              use in place of the rate we compute from your order history.
            </p>
            <SegmentedToggle
              options={[
                { label: "Max of the two", value: "max" },
                { label: "Use default", value: "default" },
                { label: "Use computed", value: "computed" },
              ]}
              value={values.returnRateMode}
              onChange={value =>
                void setFieldValue("returnRateMode", value as ReturnRateMode)
              }
            />
            <div className="flex items-center gap-3 mt-4">
              <Input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={(values.defaultReturnRate * 100).toFixed(1)}
                onChange={e => {
                  void setFieldValue(
                    "defaultReturnRate",
                    parseFloat(e.target.value) / 100,
                  )
                }}
                readOnly={values.returnRateMode === "computed"}
                className={cn(
                  "w-32",
                  values.returnRateMode === "computed" && "opacity-60",
                )}
                placeholder="Enter %"
              />
              <span className="text-system-gray-400 text-sm font-medium">
                %
              </span>
            </div>
            {values.returnRateMode === "computed" && (
              <p className="mt-2 text-xs text-system-gray-500">
                Using the return rate computed from your order history. Switch
                to &ldquo;Use default&rdquo; or &ldquo;Max of the two&rdquo; to
                apply your default return rate.
              </p>
            )}
            <ErrorBlock error={errors.defaultReturnRate} />
          </SettingCard>

          {/* Product Cost Markup Card */}
          <SettingCard
            id="product-cost-markup"
            title="Product Cost Markup as % of Order Value"
            tooltip={
              SETTINGS_CONFIG.find(s => s.id === "product-cost-markup")
                ?.description
            }
            isActive={activeSettingId === "product-cost-markup"}
          >
            <p className="text-sm text-system-gray-400 mb-4">
              Add a flat markup on top of the product cost we compute, as a % of
              order value. A 5% markup on a $100 order adds $5 of product cost.
            </p>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={(values.productCostMarkupPercent * 100).toFixed(1)}
                onChange={e => {
                  void setFieldValue(
                    "productCostMarkupPercent",
                    parseFloat(e.target.value) / 100,
                  )
                }}
                className="w-32"
                placeholder="Enter %"
              />
              <span className="text-system-gray-400 text-sm font-medium">
                %
              </span>
            </div>
            <ErrorBlock error={errors.productCostMarkupPercent} />
          </SettingCard>
        </div>
      </div>

      {/* Section: Amazon Financial Settings */}
      <div>
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <span className="w-1 h-6 bg-system-brand-pink-500 rounded-full" />
          Amazon Financial Settings
        </h2>
        <div className="space-y-4">
          {/* Amazon Discount Card */}
          <SettingCard
            id="amazon-discount"
            title="Amazon Discount %"
            tooltip={
              SETTINGS_CONFIG.find(s => s.id === "amazon-discount")?.description
            }
            isActive={activeSettingId === "amazon-discount"}
          >
            <p className="text-sm text-system-gray-400 mb-4">
              Average discount applied to Amazon orders as a % of gross sales.
            </p>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={(values.amazonDiscountPercent * 100).toFixed(1)}
                onChange={e => {
                  void setFieldValue(
                    "amazonDiscountPercent",
                    parseFloat(e.target.value) / 100,
                  )
                }}
                className="w-32"
                placeholder="Enter %"
              />
              <span className="text-system-gray-400 text-sm font-medium">
                %
              </span>
            </div>
            <ErrorBlock error={errors.amazonDiscountPercent} />
          </SettingCard>

          {/* Amazon Refund Rate Card */}
          <SettingCard
            id="amazon-refund-rate"
            title="Amazon Refund Rate"
            tooltip={
              SETTINGS_CONFIG.find(s => s.id === "amazon-refund-rate")
                ?.description
            }
            isActive={activeSettingId === "amazon-refund-rate"}
          >
            <p className="text-sm text-system-gray-400 mb-4">
              The % of Amazon gross sales refunded to customers.
            </p>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={(values.amazonRefundRate * 100).toFixed(1)}
                onChange={e => {
                  void setFieldValue(
                    "amazonRefundRate",
                    parseFloat(e.target.value) / 100,
                  )
                }}
                className="w-32"
                placeholder="Enter %"
              />
              <span className="text-system-gray-400 text-sm font-medium">
                %
              </span>
            </div>
            <ErrorBlock error={errors.amazonRefundRate} />
          </SettingCard>

          {/* Amazon Shipping Costs Card */}
          <SettingCard
            id="amazon-shipping-costs"
            title="Amazon Shipping Costs as % of Gross Sales"
            tooltip={
              SETTINGS_CONFIG.find(s => s.id === "amazon-shipping-costs")
                ?.description
            }
            isActive={activeSettingId === "amazon-shipping-costs"}
          >
            <p className="text-sm text-system-gray-400 mb-4">
              Amazon shipping and fulfillment costs as a % of gross sales.
            </p>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={(values.amazonShippingCostPercent * 100).toFixed(1)}
                onChange={e => {
                  void setFieldValue(
                    "amazonShippingCostPercent",
                    parseFloat(e.target.value) / 100,
                  )
                }}
                className="w-32"
                placeholder="Enter %"
              />
              <span className="text-system-gray-400 text-sm font-medium">
                %
              </span>
            </div>
            <ErrorBlock error={errors.amazonShippingCostPercent} />
          </SettingCard>

          {/* Amazon COGS Card */}
          <SettingCard
            id="amazon-cogs"
            title="Amazon COGS as % of Gross Sales"
            tooltip={
              SETTINGS_CONFIG.find(s => s.id === "amazon-cogs")?.description
            }
            isActive={activeSettingId === "amazon-cogs"}
          >
            <p className="text-sm text-system-gray-400 mb-4">
              Amazon cost of goods sold as a % of gross sales.
            </p>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={(values.amazonCogsPercent * 100).toFixed(1)}
                onChange={e => {
                  void setFieldValue(
                    "amazonCogsPercent",
                    parseFloat(e.target.value) / 100,
                  )
                }}
                className="w-32"
                placeholder="Enter %"
              />
              <span className="text-system-gray-400 text-sm font-medium">
                %
              </span>
            </div>
            <ErrorBlock error={errors.amazonCogsPercent} />
          </SettingCard>

          {/* Amazon CC/Seller Fees Card */}
          <SettingCard
            id="amazon-cc-fees"
            title="Amazon CC/Seller Fees as % of Gross Sales"
            tooltip={
              SETTINGS_CONFIG.find(s => s.id === "amazon-cc-fees")?.description
            }
            isActive={activeSettingId === "amazon-cc-fees"}
          >
            <p className="text-sm text-system-gray-400 mb-4">
              Credit card and Amazon seller/referral fees as a % of gross sales.
            </p>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={(values.amazonCcFeePercent * 100).toFixed(1)}
                onChange={e => {
                  void setFieldValue(
                    "amazonCcFeePercent",
                    parseFloat(e.target.value) / 100,
                  )
                }}
                className="w-32"
                placeholder="Enter %"
              />
              <span className="text-system-gray-400 text-sm font-medium">
                %
              </span>
            </div>
            <ErrorBlock error={errors.amazonCcFeePercent} />
          </SettingCard>

          {/* Amazon Ad Spend Card */}
          <SettingCard
            id="amazon-ad-spend"
            title="Amazon Ad Spend as % of Gross Sales"
            tooltip={
              SETTINGS_CONFIG.find(s => s.id === "amazon-ad-spend")?.description
            }
            isActive={activeSettingId === "amazon-ad-spend"}
          >
            <p className="text-sm text-system-gray-400 mb-4">
              Amazon advertising spend as a % of gross sales.
            </p>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={(values.amazonAdSpendPercent * 100).toFixed(1)}
                onChange={e => {
                  void setFieldValue(
                    "amazonAdSpendPercent",
                    parseFloat(e.target.value) / 100,
                  )
                }}
                className="w-32"
                placeholder="Enter %"
              />
              <span className="text-system-gray-400 text-sm font-medium">
                %
              </span>
            </div>
            <ErrorBlock error={errors.amazonAdSpendPercent} />
          </SettingCard>
        </div>
      </div>

      {/* Section: DTC Retail Financial Settings */}
      <div>
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <span className="w-1 h-6 bg-system-brand-pink-500 rounded-full" />
          DTC Retail Financial Settings
        </h2>
        <div className="space-y-4">
          {/* DTC Retail Staffing Card */}
          <SettingCard
            id="dtc-retail-staffing"
            title="DTC Retail Variable Staffing Cost as % of Sales"
            tooltip={
              SETTINGS_CONFIG.find(s => s.id === "dtc-retail-staffing")
                ?.description
            }
            isActive={activeSettingId === "dtc-retail-staffing"}
          >
            <p className="text-sm text-system-gray-400 mb-4">
              Variable staffing costs for your retail business as a % of sales.
            </p>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={(values.dtcRetailStaffingCostPercent * 100).toFixed(1)}
                onChange={e => {
                  void setFieldValue(
                    "dtcRetailStaffingCostPercent",
                    parseFloat(e.target.value) / 100,
                  )
                }}
                className="w-32"
                placeholder="Enter %"
              />
              <span className="text-system-gray-400 text-sm font-medium">
                %
              </span>
            </div>
            <ErrorBlock error={errors.dtcRetailStaffingCostPercent} />
          </SettingCard>

          {/* DTC Retail CC/Seller Fees Card */}
          <SettingCard
            id="dtc-retail-cc-fees"
            title="DTC Retail CC/Seller Fees as % of Gross Sales"
            tooltip={
              SETTINGS_CONFIG.find(s => s.id === "dtc-retail-cc-fees")
                ?.description
            }
            isActive={activeSettingId === "dtc-retail-cc-fees"}
          >
            <p className="text-sm text-system-gray-400 mb-4">
              Credit card and seller fees for your retail business as a % of
              gross sales.
            </p>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={(values.dtcRetailCcFeePercent * 100).toFixed(1)}
                onChange={e => {
                  void setFieldValue(
                    "dtcRetailCcFeePercent",
                    parseFloat(e.target.value) / 100,
                  )
                }}
                className="w-32"
                placeholder="Enter %"
              />
              <span className="text-system-gray-400 text-sm font-medium">
                %
              </span>
            </div>
            <ErrorBlock error={errors.dtcRetailCcFeePercent} />
          </SettingCard>

          {/* DTC Retail Local Marketing Card */}
          <SettingCard
            id="dtc-retail-local-marketing"
            title="DTC Retail Local Events/Community Marketing as % of Gross Sales"
            tooltip={
              SETTINGS_CONFIG.find(s => s.id === "dtc-retail-local-marketing")
                ?.description
            }
            isActive={activeSettingId === "dtc-retail-local-marketing"}
          >
            <p className="text-sm text-system-gray-400 mb-4">
              Local events and community marketing spend as a % of gross sales.
            </p>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={(values.dtcRetailLocalMarketingPercent * 100).toFixed(1)}
                onChange={e => {
                  void setFieldValue(
                    "dtcRetailLocalMarketingPercent",
                    parseFloat(e.target.value) / 100,
                  )
                }}
                className="w-32"
                placeholder="Enter %"
              />
              <span className="text-system-gray-400 text-sm font-medium">
                %
              </span>
            </div>
            <ErrorBlock error={errors.dtcRetailLocalMarketingPercent} />
          </SettingCard>

          {/* DTC Retail Other Variable Costs Card */}
          <SettingCard
            id="dtc-retail-other-costs"
            title="DTC Retail Other Variable Costs as % of Gross Sales"
            tooltip={
              SETTINGS_CONFIG.find(s => s.id === "dtc-retail-other-costs")
                ?.description
            }
            isActive={activeSettingId === "dtc-retail-other-costs"}
          >
            <p className="text-sm text-system-gray-400 mb-4">
              Other variable costs for your retail business as a % of gross
              sales.
            </p>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={(values.dtcRetailOtherVariableCostPercent * 100).toFixed(
                  1,
                )}
                onChange={e => {
                  void setFieldValue(
                    "dtcRetailOtherVariableCostPercent",
                    parseFloat(e.target.value) / 100,
                  )
                }}
                className="w-32"
                placeholder="Enter %"
              />
              <span className="text-system-gray-400 text-sm font-medium">
                %
              </span>
            </div>
            <ErrorBlock error={errors.dtcRetailOtherVariableCostPercent} />
          </SettingCard>
        </div>
      </div>

      {/* Section: ROAS & Budget Targets */}
      <div>
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <span className="w-1 h-6 bg-system-brand-pink-500 rounded-full" />
          ROAS & Budget Targets
        </h2>
        <div className="space-y-4">
          {/* Spend Guardrail Card (aMER / MER / CAC) */}
          <SettingCard
            id="target-mer"
            title="Spend Guardrail: aMER, MER, or CAC"
            tooltip={
              SETTINGS_CONFIG.find(s => s.id === "target-mer")?.description
            }
            isActive={activeSettingId === "target-mer"}
          >
            <p className="text-sm text-system-gray-400 mb-4">
              Set the target your recommended daily spend is derived from,
              expressed however you manage it.
            </p>
            <div className="flex flex-wrap gap-3 mb-4">
              <SegmentedToggle
                options={[
                  { label: "aMER", value: "amer" },
                  { label: "MER", value: "mer" },
                  { label: "CAC", value: "cac" },
                ]}
                value={values.targetMerDisplay}
                onChange={handleDisplayChange}
              />
              <SegmentedToggle
                options={[
                  { label: "Ecommerce", value: "ecomm" },
                  { label: "Total Business", value: "total" },
                ]}
                value={values.targetMerScope}
                onChange={value => {
                  setCacDraft(null)
                  void setFieldValue("targetMerScope", value as TargetMerScope)
                }}
              />
            </div>
            {isCacDisplay ? (
              <>
                <div className="flex items-center gap-3">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    prependNode={
                      <span className="text-system-gray-400 text-sm font-medium">
                        $
                      </span>
                    }
                    value={
                      cacDraft ??
                      (targetCac !== null ? targetCac.toFixed(2) : "")
                    }
                    onChange={e => {
                      setCacDraft(e.target.value)
                      void setFieldValue(
                        "targetMerValue",
                        amerFromCac(
                          parseFloat(e.target.value),
                          firstOrderValue,
                        ),
                      )
                    }}
                    onBlur={() => setCacDraft(null)}
                    disabled={firstOrderValue === null}
                    className="w-32"
                    placeholder="e.g. 65"
                  />
                </div>
                <p className="mt-2 text-xs text-system-gray-500">
                  CAC = Total Ad Spend / New Customers ({scopeLabel}).{" "}
                  {cacHelpText}
                </p>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={values.targetMerValue || ""}
                    onChange={e => {
                      void setFieldValue(
                        "targetMerValue",
                        parseFloat(e.target.value) || 0,
                      )
                    }}
                    className="w-32"
                    placeholder="e.g. 3.5"
                  />
                  <span className="text-system-gray-400 text-sm font-medium">
                    x
                  </span>
                </div>
                <p className="mt-2 text-xs text-system-gray-500">
                  {values.targetMerDisplay === "amer"
                    ? "aMER = New Customer Revenue / Total Ad Spend"
                    : "MER = Total Revenue / Total Ad Spend"}{" "}
                  ({scopeLabel})
                  {values.targetMerDisplay === "amer" &&
                    targetCac !== null &&
                    firstOrderValueLabel &&
                    `. Equivalent CAC: ${formatGuardrailValue(targetCac, "cac")} at your ${firstOrderValueLabel}.`}
                </p>
              </>
            )}
            <ErrorBlock error={errors.targetMerValue} />
          </SettingCard>

          {/* Target DR ROAS Card */}
          <SettingCard
            id="target-dr-roas"
            title="Target Direct Response ROAS"
            tooltip={
              SETTINGS_CONFIG.find(s => s.id === "target-dr-roas")?.description
            }
            isActive={activeSettingId === "target-dr-roas"}
          >
            <p className="text-sm text-system-gray-400 mb-4">
              The minimum return on ad spend you require for direct response
              campaigns to be profitable.
            </p>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                step="0.01"
                min="0"
                value={values.targetDRRoas || ""}
                onChange={e => {
                  if (values.targetDRRoasLocked) return
                  setIsCustomDRRoas(true)
                  void setFieldValue(
                    "targetDRRoas",
                    parseFloat(e.target.value) || 0,
                  )
                }}
                readOnly={values.targetDRRoasLocked}
                className={cn(
                  "w-32",
                  values.targetDRRoasLocked && "opacity-60",
                )}
                placeholder="e.g. 3.50"
              />
              <span className="text-system-gray-400 text-sm font-medium">
                x
              </span>
            </div>
            {hasRoasData && recommendedDRRoas !== null && (
              <div className="mt-3 flex items-center gap-3">
                <span className="text-sm text-system-gray-400">
                  Recommended:{" "}
                  <span className="text-system-sea-400 font-medium">
                    {recommendedDRRoas.toFixed(2)}x
                  </span>
                </span>
                {!values.targetDRRoasLocked &&
                  (isCustomDRRoas || hadSavedDRRoas) &&
                  values.targetDRRoas !== recommendedDRRoas && (
                    <button
                      type="button"
                      className="text-xs text-system-brand-pink-400 hover:text-system-brand-pink-300 underline transition-colors"
                      onClick={() => {
                        setIsCustomDRRoas(false)
                        void setFieldValue("targetDRRoas", recommendedDRRoas)
                      }}
                    >
                      Use recommended
                    </button>
                  )}
              </div>
            )}
            {hasRoasData && recommendedDRRoas !== null && (
              <LockToggle
                locked={values.targetDRRoasLocked}
                onToggle={locked => {
                  void setFieldValue("targetDRRoasLocked", locked)
                  if (locked && recommendedDRRoas > 0) {
                    setIsCustomDRRoas(false)
                    void setFieldValue("targetDRRoas", recommendedDRRoas)
                  }
                }}
              />
            )}
            {isRoasLoading && (
              <p className="mt-2 text-xs text-system-gray-500">
                Calculating recommendation...
              </p>
            )}
            <ErrorBlock error={errors.targetDRRoas} />
          </SettingCard>

          {/* Target Brand ROAS Card */}
          <SettingCard
            id="target-brand-roas"
            title="Target Brand ROAS"
            tooltip={
              SETTINGS_CONFIG.find(s => s.id === "target-brand-roas")
                ?.description
            }
            isActive={activeSettingId === "target-brand-roas"}
          >
            <p className="text-sm text-system-gray-400 mb-4">
              The minimum return on ad spend for brand awareness campaigns.
              Defaults to 1.5x your DR ROAS target.
            </p>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                step="0.01"
                min="0"
                value={values.targetBrandRoas || ""}
                onChange={e => {
                  if (values.targetBrandRoasLocked) return
                  setIsCustomBrandRoas(true)
                  void setFieldValue(
                    "targetBrandRoas",
                    parseFloat(e.target.value) || 0,
                  )
                }}
                readOnly={values.targetBrandRoasLocked}
                className={cn(
                  "w-32",
                  values.targetBrandRoasLocked && "opacity-60",
                )}
                placeholder="e.g. 5.25"
              />
              <span className="text-system-gray-400 text-sm font-medium">
                x
              </span>
            </div>
            {hasRoasData && recommendedBrandRoas !== null && (
              <div className="mt-3 flex items-center gap-3">
                <span className="text-sm text-system-gray-400">
                  Recommended:{" "}
                  <span className="text-system-sea-400 font-medium">
                    {recommendedBrandRoas.toFixed(2)}x
                  </span>
                </span>
                {!values.targetBrandRoasLocked &&
                  (isCustomBrandRoas || hadSavedBrandRoas) &&
                  values.targetBrandRoas !== recommendedBrandRoas && (
                    <button
                      type="button"
                      className="text-xs text-system-brand-pink-400 hover:text-system-brand-pink-300 underline transition-colors"
                      onClick={() => {
                        setIsCustomBrandRoas(false)
                        void setFieldValue(
                          "targetBrandRoas",
                          recommendedBrandRoas,
                        )
                      }}
                    >
                      Use recommended
                    </button>
                  )}
              </div>
            )}
            {hasRoasData && recommendedBrandRoas !== null && (
              <LockToggle
                locked={values.targetBrandRoasLocked}
                onToggle={locked => {
                  void setFieldValue("targetBrandRoasLocked", locked)
                  if (locked && recommendedBrandRoas > 0) {
                    setIsCustomBrandRoas(false)
                    void setFieldValue("targetBrandRoas", recommendedBrandRoas)
                  }
                }}
              />
            )}
            {isRoasLoading && (
              <p className="mt-2 text-xs text-system-gray-500">
                Calculating recommendation...
              </p>
            )}
            <ErrorBlock error={errors.targetBrandRoas} />
          </SettingCard>

          {/* Target Daily Budget Card */}
          <SettingCard
            id="target-daily-budget"
            title="Target Daily Budget"
            tooltip={
              SETTINGS_CONFIG.find(s => s.id === "target-daily-budget")
                ?.description
            }
            isActive={activeSettingId === "target-daily-budget"}
          >
            <p className="text-sm text-system-gray-400 mb-4">
              Your total daily advertising budget across all channels, used to
              scale budget recommendations.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-system-gray-400 text-sm font-medium">
                $
              </span>
              <Input
                type="number"
                step="100"
                min="0"
                value={values.targetDailyBudget || ""}
                onChange={e => {
                  if (values.targetDailyBudgetLocked) return
                  setIsCustomDailyBudget(true)
                  void setFieldValue(
                    "targetDailyBudget",
                    parseFloat(e.target.value) || 0,
                  )
                }}
                readOnly={values.targetDailyBudgetLocked}
                className={cn(
                  "w-40",
                  values.targetDailyBudgetLocked && "opacity-60",
                )}
                placeholder="e.g. 10000"
              />
              <span className="text-system-gray-400 text-sm font-medium">
                / day
              </span>
              <SegmentedToggle
                className="ml-2"
                options={DAILY_BUDGET_EVAL_PERIOD_OPTIONS.map(o => ({
                  value: String(o.value),
                  label: o.label,
                }))}
                value={String(values.dailyBudgetEvalPeriodDays)}
                onChange={v =>
                  void setFieldValue("dailyBudgetEvalPeriodDays", Number(v))
                }
              />
            </div>
            <p className="mt-2 text-xs text-system-gray-500">
              The recommended budget averages revenue over this evaluation
              period against your spend guardrail (aMER, MER, or CAC target).
              Only complete days are used — pick a shorter period to react
              faster to recent performance.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span className="text-system-gray-400 text-sm font-medium">
                Recent change window
              </span>
              <Input
                type="number"
                step="1"
                min="0"
                max="90"
                value={values.manualChangeHoldDays}
                onChange={e =>
                  void setFieldValue(
                    "manualChangeHoldDays",
                    Math.max(
                      0,
                      Math.min(90, parseInt(e.target.value, 10) || 0),
                    ),
                  )
                }
                className="w-24"
              />
              <span className="text-system-gray-400 text-sm font-medium">
                days
              </span>
            </div>
            <p className="mt-2 text-xs text-system-gray-500">
              Campaigns and ad sets whose budget or bid target a person or agent
              changed, that they enabled, or that first spent within this many
              days are labeled in the Budget Recommender so you can decide
              whether to include them. They are allocated like any other row;
              approving a change to one asks for an acknowledgment. 0 turns the
              labels off.
            </p>
            <ErrorBlock error={errors.manualChangeHoldDays} />
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span className="text-system-gray-400 text-sm font-medium">
                Brand search
              </span>
              <Field
                as={Select}
                name="brandSearchPolicy"
                options={BRAND_SEARCH_POLICY_OPTIONS}
                className="w-64"
              />
            </div>
            <p className="mt-2 text-xs text-system-gray-500">
              Fixed holds every Branded Search campaign at its current budget in
              the Budget Recommender, like a pin: brand search is scored against
              the DR ROAS target, where its modeled ROAS sits below target by
              construction, so Optimize cuts it on every run.
            </p>
            <ErrorBlock error={errors.brandSearchPolicy} />
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span className="text-system-gray-400 text-sm font-medium">
                Step cap
              </span>
              <Input
                type="number"
                step="1"
                min={MAX_BUDGET_STEP_PCT_MIN * 100}
                max={MAX_BUDGET_STEP_PCT_MAX * 100}
                value={
                  values.maxBudgetStepPct === null
                    ? ""
                    : Math.round(values.maxBudgetStepPct * 100)
                }
                onChange={e => {
                  const pct = parseFloat(e.target.value)
                  void setFieldValue(
                    "maxBudgetStepPct",
                    Number.isFinite(pct) ? pct / 100 : null,
                  )
                }}
                className="w-24"
                placeholder="Off"
                aria-label="Step cap percent"
              />
              <span className="text-system-gray-400 text-sm font-medium">
                % per run
              </span>
            </div>
            <p className="mt-2 text-xs text-system-gray-500">
              The most one Budget Recommender run may move a selected row, up or
              down, as a share of its current budget (10–30%). A capped row is
              never paused from above $10 ÷ (1 − cap). Leave empty for no cap; a
              row can carry its own cap.
            </p>
            <ErrorBlock error={errors.maxBudgetStepPct} />
            {hasDailyBudgetData && recommendedDailyBudget !== null && (
              <div className="mt-3 flex items-center gap-3">
                <span className="text-sm text-system-gray-400">
                  Recommended:{" "}
                  <span className="text-system-sea-400 font-medium">
                    ${recommendedDailyBudget.toLocaleString()}
                  </span>
                </span>
                {!values.targetDailyBudgetLocked &&
                  (isCustomDailyBudget || hadSavedDailyBudget) &&
                  values.targetDailyBudget !== recommendedDailyBudget && (
                    <button
                      type="button"
                      className="text-xs text-system-brand-pink-400 hover:text-system-brand-pink-300 underline transition-colors"
                      onClick={() => {
                        setIsCustomDailyBudget(false)
                        void setFieldValue(
                          "targetDailyBudget",
                          recommendedDailyBudget,
                        )
                      }}
                    >
                      Use recommended
                    </button>
                  )}
              </div>
            )}
            {hasDailyBudgetData && recommendedDailyBudget !== null && (
              <LockToggle
                locked={values.targetDailyBudgetLocked}
                onToggle={locked => {
                  void setFieldValue("targetDailyBudgetLocked", locked)
                  if (locked && recommendedDailyBudget > 0) {
                    setIsCustomDailyBudget(false)
                    void setFieldValue(
                      "targetDailyBudget",
                      recommendedDailyBudget,
                    )
                  }
                }}
              />
            )}
            {isDailyBudgetLoading && (
              <p className="mt-2 text-xs text-system-gray-500">
                Calculating recommendation...
              </p>
            )}
            <ErrorBlock error={errors.targetDailyBudget} />
          </SettingCard>
        </div>
      </div>

      {/* Section: Reporting */}
      <div>
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <span className="w-1 h-6 bg-system-brand-pink-500 rounded-full" />
          Reporting
        </h2>
        <div className="space-y-4">
          <SettingCard
            id="week-start-day"
            title="Reporting Week Start"
            tooltip={
              SETTINGS_CONFIG.find(s => s.id === "week-start-day")?.description
            }
            isActive={activeSettingId === "week-start-day"}
          >
            <p className="text-sm text-system-gray-400 mb-4">
              The first day of your reporting week. Weekly views across the
              product group data into weeks starting on this day and ending six
              days later.
            </p>
            <Field
              as={Select}
              name="weekStartDay"
              options={WEEK_START_DAY_OPTIONS}
              className="w-64"
              disabled={!canEditWeekStartDay}
            />
            {!canEditWeekStartDay && (
              <p className="mt-2 text-xs text-system-gray-500">
                Only company admins can change the reporting week start.
              </p>
            )}
            <ErrorBlock error={errors.weekStartDay} />
          </SettingCard>
        </div>
      </div>
    </Form>
  )
}

export default function FinancialSettingsPage() {
  const {
    initialValues,
    validationSchema,
    updateFinancialSettings,
    isLoading,
    isFetchingFinancials,
  } = useConnect()
  const formRef = useRef<FormikProps<TFinancialSettingsFormValues> | null>(null)

  // Changing the week start re-groups all weekly reporting company-wide, so
  // it is admin-only (the financials endpoint already enforces this server-side
  // via scopes) and gated behind a confirmation dialog on save.
  const sessionRole = useAppSelector(selectUserSessionRole)
  const canEditWeekStartDay =
    sessionRole === SessionRole.ADMIN ||
    sessionRole === SessionRole.ADMIN_WITH_ACCESS ||
    sessionRole === SessionRole.SUPER_ADMIN
  const [pendingValues, setPendingValues] =
    useState<TFinancialSettingsFormValues | null>(null)

  // Active setting for navigation and contextual help
  const [activeSettingId, setActiveSettingId] = useState<string>(
    SETTINGS_CONFIG[0].id,
  )

  // Track mounted state for intersection observer
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    document.title = "Marathon Data: Setup > Financial Settings"
    setIsMounted(true)
  }, [])

  // Set up scroll spy using intersection observer + bottom-of-page detection
  useEffect(() => {
    if (!isMounted) return

    const sectionIds = SETTINGS_CONFIG.map(s => s.id)
    const elements = sectionIds
      .map(id => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null)

    if (elements.length === 0) return

    const findLastVisibleSection = () => {
      for (let i = elements.length - 1; i >= 0; i--) {
        const rect = elements[i].getBoundingClientRect()
        if (rect.top < window.innerHeight) {
          return elements[i].id
        }
      }
      return null
    }

    const handleScroll = () => {
      const nearBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 100
      if (nearBottom) {
        const lastVisible = findLastVisibleSection()
        if (lastVisible) {
          setActiveSettingId(lastVisible)
        }
      }
    }

    const observer = new IntersectionObserver(
      entries => {
        const nearBottom =
          window.innerHeight + window.scrollY >=
          document.documentElement.scrollHeight - 100
        if (nearBottom) return

        const visibleEntries = entries.filter(entry => entry.isIntersecting)
        if (visibleEntries.length > 0) {
          const mostVisible = visibleEntries.reduce((prev, current) =>
            current.intersectionRatio > prev.intersectionRatio ? current : prev,
          )
          setActiveSettingId(mostVisible.target.id)
        }
      },
      {
        rootMargin: "-10% 0px -50% 0px",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      },
    )

    elements.forEach(element => {
      observer.observe(element)
    })
    window.addEventListener("scroll", handleScroll, { passive: true })

    return () => {
      observer.disconnect()
      window.removeEventListener("scroll", handleScroll)
    }
  }, [isMounted])

  const handleNavigate = useCallback((id: string) => {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" })
      setActiveSettingId(id)
    }
  }, [])

  const handleSubmit = async (values: TFinancialSettingsFormValues) => {
    if (values.weekStartDay !== initialValues.weekStartDay) {
      setPendingValues(values)
      return
    }
    await updateFinancialSettings(values)
  }

  const confirmWeekStartChange = async () => {
    if (!pendingValues) return
    const values = pendingValues
    setPendingValues(null)
    await updateFinancialSettings(values)
  }

  const { isEnabled: isDemoEnabled } = useDemoMode()

  return (
    <div className="min-h-screen">
      <div className="text-start mb-6">
        <Breadcrumbs
          items={[
            {
              slug: "/setup",
              name: "Setup",
            },
            {
              slug: "financial-settings",
              name: "Financial Settings",
            },
          ]}
        />
      </div>

      <div
        className={cn(
          "grid grid-cols-[260px_1fr] gap-6 items-start",
          isDemoEnabled && "blur-md",
        )}
      >
        <PageNavigation
          activeId={activeSettingId}
          onNavigate={handleNavigate}
          onSave={() => formRef.current?.submitForm()}
          isSaving={isLoading}
          isDisabled={isFetchingFinancials}
        />

        <div className="pr-2">
          {isFetchingFinancials ? (
            <LoadingSkeleton />
          ) : (
            <Formik
              initialValues={initialValues}
              onSubmit={handleSubmit}
              validationSchema={validationSchema}
              enableReinitialize
              innerRef={formRef}
              validateOnBlur={true}
              validateOnChange={true}
            >
              {({ values, errors, setFieldValue }) => (
                <FormContent
                  values={values}
                  errors={errors}
                  setFieldValue={setFieldValue}
                  activeSettingId={activeSettingId}
                  canEditWeekStartDay={canEditWeekStartDay}
                />
              )}
            </Formik>
          )}
        </div>
      </div>

      <Dialog
        isOpen={pendingValues !== null}
        handleClose={() => setPendingValues(null)}
      >
        <div className="p-6 max-w-md">
          <h3 className="text-lg font-semibold text-white mb-3">
            Change reporting week start?
          </h3>
          <p className="text-sm text-system-gray-400 mb-2">
            Weeks will start on{" "}
            <span className="text-white font-medium">
              {pendingValues ? capitalize(pendingValues.weekStartDay) : ""}
            </span>{" "}
            for everyone in your company.
          </p>
          <p className="text-sm text-system-gray-400 mb-2">
            All weekly reporting — including historical data — immediately
            re-groups into the new weeks after saving.
          </p>
          <p className="text-sm text-system-gray-400 mb-6">
            Cancelling keeps your edits in the form but saves nothing —
            including changes to other settings.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant={{ variant: "outlined" }}
              size="sm"
              onClick={() => setPendingValues(null)}
            >
              Cancel
            </Button>
            <Button
              variant={{ variant: "solid", color: "pink" }}
              size="sm"
              onClick={() => void confirmWeekStartChange()}
            >
              Change week start
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  )
}
